import loadable from "@loadable/component";
import { IS_PROD, IS_DEV } from "../constants/env";

const LayoutDefault = loadable(() => import("./layouts/LayoutDefault"));

const Index = loadable(() => import("./pages/Index"));
const Drama = loadable(() => import("./pages/Drama"));
const IframePlayer = loadable(() => import("./pages/IframePlayer"));
const EmbedPlayer = loadable(() => import("./pages/EmbedPlayer"));

const Login = loadable(() => import("./pages/OauthCallback"));
const EmailLogin = loadable(() => import("./pages/EmailLogin"));
const ForgetPassword = loadable(() => import("./pages/ForgetPassword"));
const FirebaseAuthPhone = loadable(() => import("./pages/FirebaseAuthPhone"));

const User = loadable(() => import("./pages/User"));
const UserProfile = loadable(() => import("./pages/UserProfile"));
const UserSubscription = loadable(() => import("./pages/UserSubscription"));
const UserWatchHistory = loadable(() => import("./pages/UserWatchHistory"));
const UserAccountBinding = loadable(() => import("./pages/UserAccountBinding"));
const UserFavorite = loadable(() => import("./pages/UserFavorite"));

const CommunityBinding = loadable(() =>
  import("./containers/Community/CommunityBinding")
);
const LineOaBinding = loadable(() => import("./pages/LineOaBinding"));

const Purchase = loadable(() => import("./pages/Purchase"));
const PurchaseVip = loadable(() => import("./pages/Purchase/PurchaseVip"));
const PurchasePackageGroups = loadable(() =>
  import("./pages/Purchase/PurchasePackageGroups")
);

const LayoutPayment = loadable(() => import("./layouts/LayoutPayment"));
const Payment = loadable(() => import("./pages/Payment"));

const PaymentVipOrder = loadable(() =>
  import("./pages/PaymentVip/PaymentVipOrder")
);
const PaymentVipFormTappay = loadable(() =>
  import("./pages/PaymentVip/PaymentVipFormTappay")
);
const PaymentVipFormLinepay = loadable(() =>
  import("./pages/PaymentVip/PaymentVipFormLinepay")
);
const PaymentVipResultLinepay = loadable(() =>
  import("./pages/PaymentVip/PaymentVipResultLinepay")
);
const PaymentVipResultTappay = loadable(() =>
  import("./pages/PaymentVip/PaymentVipResultTappay")
);
const PaymentVipResultSonet = loadable(() =>
  import("./pages/PaymentVip/PaymentVipResultSonet")
);

const PaymentPackageOrder = loadable(() =>
  import("./pages/PaymentPackage/PaymentPackageOrder")
);
const PaymentPackageFormTappay = loadable(() =>
  import("./pages/PaymentPackage/PaymentPackageFormTappay")
);
const PaymentPackageFormLinepay = loadable(() =>
  import("./pages/PaymentPackage/PaymentPackageFormLinepay")
);
const PaymentPackageResultTappay = loadable(() =>
  import("./pages/PaymentPackage/PaymentPackageResultTappay")
);
const PaymentPackageResultLinepay = loadable(() =>
  import("./pages/PaymentPackage/PaymentPackageResultLinepay")
);

const PaymentRedeemOrder = loadable(() =>
  import("./pages/PaymentRedeem/PaymentRedeemOrder")
);
const PaymentRedeemFormTappay = loadable(() =>
  import("./pages/PaymentRedeem/PaymentRedeemFormTappay")
);
const PaymentRedeemFormLinepay = loadable(() =>
  import("./pages/PaymentRedeem/PaymentRedeemFormLinepay")
);
const PaymentRedeemResultTappay = loadable(() =>
  import("./pages/PaymentRedeem/PaymentRedeemResultTappay")
);
const PaymentRedeemResultLinepay = loadable(() =>
  import("./pages/PaymentRedeem/PaymentRedeemResultLinepay")
);

const Redeem = loadable(() => import("./pages/Redeem/Redeem"));
const RedeemResult = loadable(() =>
  import("./pages/RedeemResult/RedeemResult")
);

const LineMobileServices = loadable(() => import("./pages/LineMobileServices"));
const LineMobileCheckout = loadable(() => import("./pages/LineMobileCheckout"));
const LineMobileOTP = loadable(() => import("./pages/LineMobileOTP"));

const Feed = loadable(() => import("./pages/Feed"));
const FeedCampaign = loadable(() => import("./pages/FeedCampaign"));
const FeedNews = loadable(() => import("./pages/FeedNews"));

const ChannelHome = loadable(() => import("./pages/ChannelHome"));
const ChannelGroup = loadable(() => import("./pages/ChannelGroup"));
const ChannelFilter = loadable(() => import("./pages/ChannelFilter"));

const SearchResult = loadable(() => import("./pages/SearchResult"));

const Maintain = loadable(() => import("./containers/Maintain"));
const ErrorMessage = loadable(() => import("./components/ErrorMessage"));

const DramaMobileAmp = loadable(() => import("./pages/Drama/DramaMobileAmp"));

import redirectWithStatus from "./hoc/redirectWithStatus";
import withPageview from "./hoc/withPageview";
import withFetchData from "./hoc/withFetchData";
import renderChannelListNav from "./hoc/renderChannelListNav";
import withAuthGuard from "./hoc/withAuthGuard";
import renderSmartBanner from "./hoc/renderSmartBanner";
import withSearchIndex from "./hoc/withSearchIndex";
import withErrorBoundary from "./hoc/withErrorBoundary";
import withVIPGuard from "./hoc/withVIPGuard";

import { fetchDramaPageData, fetchIframePlayerData } from "./fetchData";

const routes = [
  {
    path: "/amp/drama/:dramaId(\\d+)/eps/:videoId(\\d+)",
    component: DramaMobileAmp,
    exact: true,
    hasSearchIndex: true,
  },
  {
    path: "/drama",
    component: redirectWithStatus("/", 301),
    exact: true,
  },
  {
    path: "/drama/:dramaId(\\d+)/:videoId(\\d+)",
    component: redirectWithStatus("/drama/:dramaId(\\d+)/eps/:videoId(\\d+)"),
    exact: true,
  },
  {
    path: "/drama/:dramaId(\\d+)",
    component: redirectWithStatus("/drama/:dramaId(\\d+)/eps/1"),
    exact: true,
  },
  {
    path: "/profile",
    component: redirectWithStatus("/user/profile"),
    exact: true,
  },
  {
    path: "/news",
    component: redirectWithStatus("/feed/news"),
    exact: true,
  },
  {
    path: "/campaign/serial",
    component: redirectWithStatus("/redeem"),
    exact: true,
  },
  {
    path: "/player/dc/:dramaId(\\d+)/:videoType(eps|behind)/:videoId(\\d+)",
    component: withFetchData(IframePlayer, fetchIframePlayerData),
    exact: true,
  },
  {
    path: "/player/:dramaId(\\d+)/:videoType(eps|behind)/:videoId(\\d+)",
    component: withFetchData(IframePlayer, fetchIframePlayerData),
    exact: true,
  },
  {
    path: "/embed/:dramaId(\\d+)/:videoType(eps|behind)/:videoId(\\d+)",
    component: withFetchData(EmbedPlayer, fetchIframePlayerData),
    exact: true,
  },
  {
    path: "/login/firebase-phone",
    component: FirebaseAuthPhone,
    exact: true,
  },
  {
    path: "/community/bundling",
    component: CommunityBinding,
    exact: true,
  },
  {
    path: "/community/line-oa/binding",
    component: LineOaBinding,
    exact: true,
    isPrivate: true,
  },
  {
    path: "/payment/:model(redeem)/:step(order|form|result)",
    isPrivate: true,
    component: LayoutPayment,
    routes: [
      // 序號綁付款
      {
        path: "/payment/redeem/order",
        component: PaymentRedeemOrder,
        exact: true,
      },
      {
        path: "/payment/redeem/form/tappay",
        component: PaymentRedeemFormTappay,
        exact: true,
      },
      {
        path: "/payment/redeem/form/linepay",
        component: PaymentRedeemFormLinepay,
        exact: true,
      },
      {
        path: "/payment/redeem/result/tappay",
        component: PaymentRedeemResultTappay,
        exact: true,
      },
      {
        path: "/payment/redeem/result/linepay",
        component: PaymentRedeemResultLinepay,
        exact: true,
      },
    ],
  },
  {
    path: "/payment/:model(vip|package)/:step(order|form|result)",
    isPrivate: true,
    component: Payment,
    routes: [
      // VIP 訂閱
      {
        path: "/payment/vip/order",
        component: PaymentVipOrder,
        exact: true,
      },
      {
        path: "/payment/vip/form/tappay",
        component: PaymentVipFormTappay,
        exact: true,
      },
      {
        path: "/payment/vip/form/linepay",
        component: PaymentVipFormLinepay,
        exact: true,
      },
      {
        path: "/payment/vip/result/linepay",
        component: PaymentVipResultLinepay,
        exact: true,
      },
      {
        path: "/payment/vip/result/tappay",
        component: PaymentVipResultTappay,
        exact: true,
      },
      {
        path: "/payment/vip/result/sonet",
        component: PaymentVipResultSonet,
        exact: true,
      },

      // 主題訂閱
      {
        path: "/payment/package/order",
        component: PaymentPackageOrder,
        exact: true,
      },
      {
        path: "/payment/package/form/tappay",
        component: PaymentPackageFormTappay,
        exact: true,
      },
      {
        path: "/payment/package/form/linepay",
        component: PaymentPackageFormLinepay,
        exact: true,
      },
      {
        path: "/payment/package/result/tappay",
        component: PaymentPackageResultTappay,
        exact: true,
      },
      {
        path: "/payment/package/result/linepay",
        component: PaymentPackageResultLinepay,
        exact: true,
      },
    ],
  },
  {
    path: "/login",
    component: Login,
    exact: true,
    isAnonymousOnly: true,
  },
  {
    component: renderSmartBanner(LayoutDefault),
    routes: [
      {
        path: "/",
        component: renderChannelListNav(Index),
        exact: true,
        hasSearchIndex: true,
      },
      {
        path: "/drama/:dramaId(\\d+)/:videoType(eps|behind)/:videoId(\\d+)",
        component: withFetchData(Drama, fetchDramaPageData),
        exact: true,
        hasSearchIndex: true,
      },

      {
        path: "/login/email",
        component: EmailLogin,
        exact: true,
        isAnonymousOnly: true,
      },
      {
        path: "/login/email/forget_password",
        component: ForgetPassword,
        exact: true,
        isAnonymousOnly: true,
      },
      {
        path: "/user",
        isPrivate: true,
        component: User,
        routes: [
          {
            path: "/user/profile",
            component: UserProfile,
            exact: true,
          },
          {
            path: "/user/subscription",
            component: UserSubscription,
            exact: true,
          },
          {
            path: "/user/watch-history",
            component: UserWatchHistory,
            exact: true,
          },
          {
            path: "/user/my-favorite",
            component: UserFavorite,
            exact: true,
          },
          {
            path: "/user/account-binding",
            component: UserAccountBinding,
            exact: true,
          },
        ],
      },
      {
        path: "/purchase",
        component: Purchase,
        hasSearchIndex: true,
        routes: [
          {
            path: "/purchase/package-group",
            component: PurchasePackageGroups,
            // exact: true,
          },
          {
            path: "/purchase",
            component: PurchaseVip,
            // exact: true,
          },
        ],
      },
      {
        path: "/redeem",
        component: Redeem,
        exact: true,
        isPrivate: true,
      },
      {
        path: "/redeem/result",
        component: RedeemResult,
        exact: true,
        isPrivate: true,
      },
      {
        path: "/line-mobile/services",
        component: withPageview(LineMobileServices, {
          title: "LINE Mobile Launch Page",
        }),
        exact: true,
      },
      {
        path: "/line-mobile/checkout",
        component: withPageview(LineMobileCheckout, {
          title: "LINE Mobile Order Page",
        }),
        exact: true,
        isPrivate: true,
      },
      {
        path: "/line-mobile/otp",
        component: withPageview(LineMobileOTP, {
          title: "LINE Mobile OTP Page",
        }),
        exact: true,
        isPrivate: true,
      },
      {
        path: "/feed",
        component: Feed,
        hasSearchIndex: true,
        routes: [
          {
            path: "/feed/campaign",
            component: FeedCampaign,
            exact: true,
          },
          {
            path: "/feed/news",
            component: FeedNews,
            exact: true,
          },
        ],
      },
      {
        path: "/channel/:channelId(\\d+)",
        exact: true,
        component: renderChannelListNav(ChannelHome),
        hasSearchIndex: true,
      },
      {
        path: "/channel-group/:groupId(\\d+)",
        exact: true,
        component: ChannelGroup,
        hasSearchIndex: true,
      },
      {
        path: "/channel/:channelId(\\d+)/genre/:genreToken?",
        exact: true,
        component: ChannelFilter,
        hasSearchIndex: true,
      },
      {
        path: "/search",
        component: SearchResult,
        exact: true,
      },
      {
        path: "/maintain",
        component: Maintain,
        exact: true,
      },
      {
        component: ErrorMessage,
      },
    ],
  },
];

function applyAuthGuard(route) {
  if (route.isPrivate || route.isAnonymousOnly) {
    route.component = withAuthGuard(route.component);
  }
}

function applySearchIndex(route) {
  if (route.hasSearchIndex && (IS_PROD || IS_DEV)) {
    route.component = withSearchIndex(route.component);
  }
}

function applyErrorBoundary(route) {
  route.component = withErrorBoundary(route.component);
}

function applyVIPGuard(route) {
  route.component = withVIPGuard(route.component);
}

function applyHOC(routes) {
  if (Array.isArray(routes)) {
    routes.forEach((route) => {
      [
        applyAuthGuard,
        applySearchIndex,
        applyErrorBoundary,
        applyVIPGuard,
      ].forEach((fn) => fn(route));
      applyHOC(route.routes);
    });
  }
}

applyHOC(routes);

export default routes;
